import React, { useState } from 'react';
import { Hash, Volume2, ChevronDown, ChevronRight, Plus, Mic, MicOff, Headphones, Settings, Radio, Users, Lock, Bell, Search, Zap, Calendar, Sparkles } from 'lucide-react';
import './ChannelSidebar.css';

const serverData = {
  0: {
    name: 'Home',
    categories: [
      {
        name: 'Text Channels',
        channels: [
          { id: 0, name: 'general', type: 'text' },
          { id: 1, name: 'announcements', type: 'text' },
          { id: 2, name: 'off-topic', type: 'text' },
        ]
      },
      {
        name: 'Voice Channels',
        channels: [
          { id: 3, name: 'General Voice', type: 'voice', users: ['Alex', 'Jordan'] },
          { id: 4, name: 'Gaming', type: 'voice', users: [] },
          { id: 5, name: 'Music', type: 'voice', users: ['Sam'] },
        ]
      }
    ]
  },
  1: {
    name: 'Gaming Hub',
    categories: [
      {
        name: 'Chat',
        channels: [
          { id: 0, name: 'lobby', type: 'text' },
          { id: 1, name: 'lfg', type: 'text' },
          { id: 2, name: 'clips', type: 'text' },
        ]
      },
      {
        name: 'Voice',
        channels: [
          { id: 3, name: 'Squad 1', type: 'voice', users: ['Player1', 'Player2', 'Player3'] },
          { id: 4, name: 'Squad 2', type: 'voice', users: [] },
        ]
      }
    ]
  },
  2: {
    name: 'Dev Community',
    categories: [
      {
        name: 'Discussion',
        channels: [
          { id: 0, name: 'help', type: 'text' },
          { id: 1, name: 'showcase', type: 'text' },
          { id: 2, name: 'resources', type: 'text' },
        ]
      },
      {
        name: 'Collaboration',
        channels: [
          { id: 3, name: 'Pair Programming', type: 'voice', users: [] },
          { id: 4, name: 'Code Review', type: 'voice', users: ['Dev1'] },
        ]
      }
    ]
  },
  3: {
    name: 'Music Lounge',
    categories: [
      {
        name: 'Chat',
        channels: [
          { id: 0, name: 'music-chat', type: 'text' },
          { id: 1, name: 'share-music', type: 'text' },
        ]
      },
      {
        name: 'Listening Party',
        channels: [
          { id: 2, name: 'Lofi Beats', type: 'voice', users: ['DJ_Mike', 'Listener1'] },
          { id: 3, name: 'Rock Zone', type: 'voice', users: [] },
        ]
      }
    ]
  },
  4: {
    name: 'Art Gallery',
    categories: [
      {
        name: 'Showcase',
        channels: [
          { id: 0, name: 'digital-art', type: 'text' },
          { id: 1, name: 'traditional', type: 'text' },
          { id: 2, name: 'feedback', type: 'text' },
        ]
      },
      {
        name: 'Live Sessions',
        channels: [
          { id: 3, name: 'Drawing Stream', type: 'voice', users: ['Artist'] },
        ]
      }
    ]
  }
};

function ChannelSidebar({ serverId, selectedChannel, onSelectChannel, onVoiceConnect, voiceChannel, username, status, isCommander, commanderActive, onCommanderToggle, onOpenSettings }) {
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const server = serverData[serverId] || serverData[0];

  const toggleCategory = (categoryName) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const handleChannelClick = (channel) => {
    if (channel.type === 'voice') {
      onVoiceConnect(channel);
    }
    onSelectChannel(channel);
  };

  const getStatusColor = () => {
    switch (status) {
      case 'online': return '#00d4aa';
      case 'idle': return '#ffb800';
      case 'dnd': return '#ff4757';
      case 'invisible': return '#556677';
      default: return '#00d4aa';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'online': return 'Online';
      case 'idle': return 'Away';
      case 'dnd': return 'Do Not Disturb';
      case 'invisible': return 'Invisible';
      default: return 'Online';
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleDeafen = () => {
    setIsDeafened(!isDeafened);
    if (!isDeafened) setIsMuted(true);
  };

  return (
    <div className="channel-sidebar">
      <div className="server-header">
        <div className="server-header-content">
          <span className="server-name">{server.name}</span>
          <span className="server-badge">
            <Users size={12} />
            {server.categories.reduce((acc, cat) => acc + cat.channels.length, 0)}
          </span>
        </div>
        <button className="server-menu-btn">
          <ChevronDown size={18} />
        </button>
      </div>

      <div className="channel-search">
        <Search size={16} />
        <input 
          type="text" 
          placeholder="Search channels..." 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="channels-list">
        {server.categories.map((category) => (
          <div key={category.name} className="channel-category">
            <div 
              className="category-header"
              onClick={() => toggleCategory(category.name)}
            >
              <div className="category-left">
                {collapsedCategories[category.name] ? 
                  <ChevronRight size={12} /> : 
                  <ChevronDown size={12} />
                }
                <span>{category.name}</span>
              </div>
              <button className="add-channel-btn" onClick={(e) => e.stopPropagation()}>
                <Plus size={14} />
              </button>
            </div>
            
            {!collapsedCategories[category.name] && (
              <div className="category-channels">
                {category.channels.map((channel) => {
                  const isActive = selectedChannel.type === channel.type && selectedChannel.id === channel.id;
                  const isVoiceConnected = voiceChannel?.id === channel.id && channel.type === 'voice';
                  
                  return (
                    <div key={`${channel.type}-${channel.id}`} className="channel-wrapper">
                      <div
                        className={`channel-item ${isActive ? 'active' : ''} ${isVoiceConnected ? 'voice-connected' : ''}`}
                        onClick={() => handleChannelClick(channel)}
                      >
                        <div className="channel-icon-wrapper">
                          {channel.type === 'text' ? 
                            <Hash size={18} className="channel-icon" /> : 
                            <Volume2 size={18} className="channel-icon voice" />
                          }
                        </div>
                        <span className="channel-name">{channel.name}</span>
                        {channel.type === 'voice' && channel.users?.length > 0 && (
                          <span className="channel-user-count">{channel.users.length}</span>
                        )}
                        <div className="channel-actions">
                          <Bell size={14} />
                          <Settings size={14} />
                        </div>
                      </div>
                      {channel.type === 'voice' && channel.users && channel.users.length > 0 && (
                        <div className="voice-users">
                          {channel.users.map((user, idx) => (
                            <div key={idx} className="voice-user">
                              <div className="voice-user-avatar">
                                <span>{user[0]}</span>
                                <div className="voice-user-status" />
                              </div>
                              <span className="voice-user-name">{user}</span>
                              <Radio size={12} className="speaking-indicator" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {isCommander && (
          <div className="commander-section">
            <div className="category-header">
              <div className="category-left">
                <Radio size={12} className="commander-icon" />
                <span>Commander Channel</span>
              </div>
            </div>
            <div 
              className={`channel-item commander-channel ${commanderActive ? 'active' : ''}`}
              onClick={onCommanderToggle}
            >
              <div className="channel-icon-wrapper commander">
                <Radio size={18} className="channel-icon" />
              </div>
              <span className="channel-name">Commander Voice</span>
              {commanderActive && <span className="channel-user-count live">LIVE</span>}
            </div>
          </div>
        )}

        <div className="sidebar-extras">
          <div className="server-boost-banner">
            <div className="boost-icon">
              <Zap size={16} />
            </div>
            <div className="boost-content">
              <span className="boost-title">Server Boost</span>
              <span className="boost-level">Level 2 • 7 Boosts</span>
            </div>
            <Sparkles size={14} className="boost-sparkle" />
          </div>

          <div className="upcoming-events">
            <div className="events-header">
              <Calendar size={12} />
              <span>Upcoming Events</span>
            </div>
            <div className="event-item">
              <div className="event-dot"></div>
              <div className="event-info">
                <span className="event-name">Game Night</span>
                <span className="event-time">Today, 8:00 PM</span>
              </div>
            </div>
            <div className="event-item">
              <div className="event-dot secondary"></div>
              <div className="event-info">
                <span className="event-name">Community Call</span>
                <span className="event-time">Tomorrow, 3:00 PM</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="user-panel">
        <div className="user-info">
          <div className="user-avatar">
            <span>{username[0].toUpperCase()}</span>
            <div className="status-indicator" style={{ background: getStatusColor(), boxShadow: `0 0 8px ${getStatusColor()}` }} />
          </div>
          <div className="user-details">
            <span className="user-name">{username}</span>
            <span className="user-status">{getStatusText()}</span>
          </div>
        </div>
        <div className="user-controls">
          <button 
            className={`control-button ${isMuted ? 'muted' : ''}`}
            onClick={toggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
          <button 
            className={`control-button ${isDeafened ? 'muted' : ''}`}
            onClick={toggleDeafen}
            title={isDeafened ? 'Undeafen' : 'Deafen'}
          >
            <Headphones size={18} />
          </button>
          <button className="control-button" title="Settings" onClick={onOpenSettings}>
            <Settings size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChannelSidebar;
